import ApiExtension from './api-extension'
import { batchPromises } from '../async'
import { getFileExtension } from '../string-utils'
import S3Multipart from '../utils/s3-multipart'

export default class Admin extends ApiExtension {
  getProducts() {
    return this.apiBase.fetch('admin_products')
  }

  getUser(userId) {
    return this.apiBase.fetch('admin_user', { id: userId })
  }

  canCreateUser() {
    return this.fetch('can_create_admin_users')
      .then(() => true)
      .catch(err => {
        if (err.response?.status === 402) {
          return false
        } else {
          throw err
        }
      })
  }

  updateUser(userId, update) {
    return this.apiBase.fetch(
      'admin_user',
      { id: userId },
      { method: 'PUT', body: { user: update } }
    )
  }

  addUserProduct(userId, productId, currency) {
    return this.apiBase.fetch(
      'admin_user_products',
      { user_id: userId },
      { method: 'POST', body: { productId, currency } }
    )
  }

  getOrganisation(organisationId) {
    return this.apiBase.fetch('admin_organisation', { id: organisationId })
  }

  updateOrganisation(organisationId, update) {
    return this.apiBase.fetch(
      'admin_organisation',
      { id: organisationId },
      { method: 'PUT', body: { organisation: update } }
    )
  }

  getJobs() {
    return this.apiBase.fetch('jobs_admin_dashboard_index')
  }

  getWorkers() {
    return this.apiBase.fetch('workers_admin_dashboard_index')
  }

  getJob(jobId) {
    return this.apiBase.fetch('admin_job', { id: jobId })
  }

  updateJob(jobId, update) {
    return this.apiBase.fetch(
      'admin_job',
      { id: jobId },
      { method: 'PUT', body: { job: update } }
    )
  }

  restartJob(jobId) {
    return this.apiBase.fetch('restart_admin_job', {
      id: jobId,
    })
  }

  getPipelineJobJson(projectId) {
    return this.apiBase.fetch('pipeline_job_admin_project', {
      id: projectId,
    })
  }

  getQuotaEvents(userId) {
    return this.apiBase.fetch('admin_user_quota_events', { user_id: userId })
  }

  uploadPlantCounts(projectId, detectionId, files, onProgress) {
    let completedUploadedBytes = 0

    return this.fetch(
      'get_dfy_plant_count_id_admin_project',
      {
        id: projectId,
      },
      null,
      { queryParams: { detection_id: detectionId } }
    )
      .then(({ job_id: jobId }) => {
        const keyPrefix = `jobs/${jobId}/`
        return batchPromises(
          files,
          file =>
            this.multipartUpload(
              projectId,
              file,
              keyPrefix + `${jobId}.${getFileExtension(file.name)}`
            ).then(() => {
              completedUploadedBytes += file.size
              onProgress && onProgress(completedUploadedBytes)
            }),
          1
        )
      })
      .then(() =>
        this.fetch(
          'publish_dfy_plant_count_admin_project',
          { id: projectId },
          { method: 'POST', body: { detectionId } }
        )
      )
  }

  userPlantCountUsageReport(userId, startDate, endDate) {
    return this.fetch(
      'plant_count_usage_report_admin_user',
      {
        id: userId,
      },
      null,
      {
        queryParams: {
          start_date: startDate,
          end_date: endDate,
        },
      }
    )
  }

  organisationPlantCountUsageReport(organisationId, startDate, endDate) {
    return this.fetch(
      'plant_count_usage_report_admin_organisation',
      {
        id: organisationId,
      },
      null,
      {
        queryParams: {
          start_date: startDate,
          end_date: endDate,
        },
      }
    )
  }

  multipartUpload(projectId, file, relativeKey, onProgress) {
    const s3Upload = new S3Multipart({
      createUpload: f =>
        this.fetch(
          'create_project_multipart_admin_project',
          { id: projectId },
          {
            method: 'POST',
            body: {
              relativeKey,
            },
          }
        ),
      getPartUrl: (f, uploadId, partNumber, partSize) =>
        this.fetch(
          'project_multipart_part_url_admin_project',
          { id: projectId },
          {
            method: 'POST',
            body: {
              relativeKey,
              uploadId,
              partNumber,
              partSize,
            },
          }
        ),
      completeUpload: (f, uploadId, etags) =>
        this.fetch(
          'complete_project_multipart_admin_project',
          { id: projectId },
          {
            method: 'POST',
            body: {
              relativeKey,
              uploadId,
              etags,
            },
          }
        ),
      onProgress,
    })

    return s3Upload.upload(file)
  }

  getProjectResourceUrl(projectId, path, method = 'GET') {
    return this.fetch('presign_url_admin_project', { id: projectId }, null, {
      queryParams: { method, path },
    })
  }

  createQuotaEvent(userId, quotaEvent) {
    return this.fetch(
      'admin_user_quota_events',
      { user_id: userId },
      { method: 'POST', body: { quotaEvent } }
    )
  }

  deletePlantDetection(projectId, detectionId) {
    return this.fetch(
      'admin_project_plant_detection',
      { project_id: projectId, id: detectionId },
      { method: 'DELETE' }
    )
  }

  getRevenue(from, to) {
    return this.fetch('revenue_admin_dashboard_index', {}, null, {
      queryParams: { from, to },
    })
  }
}
